import { cn } from "../../lib/cn";
import { CARD_EYEBROW } from "../../primitives/type";
import { AttentionPanel, PANEL_ROW, panelRowStyle } from "./panel";
import type { AttentionReflectionRow, OpacityMix } from "./types";

/**
 * The member's cycle look-back, in the slot signal rows occupy on every other
 * card. A ledger rather than the plan mosaic because a focus area has no poster,
 * and that mosaic promotes an image into its hero tile precisely so the tile is
 * never an empty frame.
 *
 * No motion: an earlier pass filled a level-progress arc per row, which reads
 * backwards, since progress resets the moment a level is earned and the member
 * with the most practice got the emptiest ring.
 */

const DAY_CELL_PX = 10;

export function AttentionReflectionLookback({
  cycleNumber,
  opacityMix,
  pending = false,
  rows,
}: {
  cycleNumber?: number | null;
  opacityMix: OpacityMix;
  pending?: boolean;
  rows: AttentionReflectionRow[];
}) {
  // Rendering nothing beats a zero state that reads as a scolding on the one
  // day we ask the member to look back.
  if (!pending && rows.length === 0) return null;

  const weekLength = rows[0]?.week.length ?? 7;

  return (
    <AttentionPanel
      label={`Last ${weekLength} days`}
      aside={cycleNumber ? `Cycle ${cycleNumber}` : undefined}
      busy={pending}
      opacityMix={opacityMix}
    >
      {pending ? (
        <LookbackSkeleton opacityMix={opacityMix} />
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {rows.map((row) => (
            <li key={row.id} className={PANEL_ROW} style={panelRowStyle}>
              {/* The whole row is one spoken sentence: read as separate nodes
                  the level chip says "L V 3" and the count repeats the strip. */}
              <div aria-hidden="true" className="flex items-baseline justify-between gap-3">
                <span className="flex min-w-0 items-baseline gap-2">
                  <span
                    className="min-w-0 truncate font-sans text-base font-medium leading-snug tracking-tight"
                    style={{ color: opacityMix(96) }}
                  >
                    {row.label}
                  </span>
                  <LevelChip level={row.level} opacityMix={opacityMix} />
                </span>
                <span
                  className="whitespace-nowrap font-mono text-2xs tabular-nums"
                  style={{ color: opacityMix(78) }}
                >
                  {row.daysLabel}
                </span>
              </div>
              {row.outcome ? (
                <p
                  aria-hidden="true"
                  className="m-0 mt-1 text-xs leading-relaxed"
                  style={{ color: opacityMix(76) }}
                >
                  {row.outcome}
                </p>
              ) : null}
              <WeekStrip opacityMix={opacityMix} week={row.week} />
              <span className="sr-only">{row.accessibleLabel}</span>
            </li>
          ))}
        </ul>
      )}
    </AttentionPanel>
  );
}

/**
 * Fixed-size cells, so the columns line up between rows and reading down a
 * column shows a day the member showed up for everything, or for nothing.
 * Stretched to the row width they read as one broken progress bar instead.
 * Hidden from assistive tech: it restates the "5 of 7 days" count beside it.
 */
function WeekStrip({ opacityMix, week }: { opacityMix: OpacityMix; week: boolean[] }) {
  return (
    <span
      aria-hidden="true"
      className="mt-2.5 grid gap-1"
      style={{ gridTemplateColumns: `repeat(${week.length}, ${DAY_CELL_PX}px)` }}
    >
      {week.map((active, index) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length trailing-day strip
          key={`day-${index}`}
          className="rounded-full"
          style={{ height: DAY_CELL_PX, background: opacityMix(active ? 92 : 24) }}
        />
      ))}
    </span>
  );
}

function LevelChip({ level, opacityMix }: { level: number; opacityMix: OpacityMix }) {
  return (
    <span
      className={cn(
        CARD_EYEBROW,
        "shrink-0 whitespace-nowrap rounded-full border px-1.5 py-0.5 leading-none tabular-nums",
      )}
      style={{ borderColor: opacityMix(24), color: opacityMix(78) }}
    >
      Lv {level}
    </span>
  );
}

function LookbackSkeleton({ opacityMix }: { opacityMix: OpacityMix }) {
  return (
    <ul className="m-0 flex list-none flex-col gap-4 p-0">
      {(["a", "b"] as const).map((slot) => (
        <li key={slot} className="flex flex-col gap-2.5">
          <span className="block h-4 w-1/2 rounded" style={{ background: opacityMix(14) }} />
          <span
            className="grid gap-1"
            style={{ gridTemplateColumns: `repeat(7, ${DAY_CELL_PX}px)` }}
          >
            {(["a", "b", "c", "d", "e", "f", "g"] as const).map((cell) => (
              <span
                key={cell}
                className="rounded-full"
                style={{ height: DAY_CELL_PX, background: opacityMix(14) }}
              />
            ))}
          </span>
        </li>
      ))}
    </ul>
  );
}
